/**
 * The set-a-password banner's memory (Aran, 2026-10-16, plan d/7bb061aa step 4,
 * mockup d/fb71062f state C).
 *
 * After somebody signs in BY CODE and their account has no password, the
 * player offers one: "Set a password for reliable access to your account".
 * This file holds the two facts that needs, on this device only:
 *
 *   1. that the last sign-in here was by code, and for which address — written
 *      by the sign-in box on a verified code, cleared on a password sign-in;
 *   2. that the learner said "Not now" or closed it — remembered per account
 *      for DISMISS_DAYS, after which a still-passwordless account is asked once
 *      more.
 *
 * Whether the account has a password is never stored here: it is read live
 * from `user_metadata.has_password`, the flag the Settings form itself writes,
 * so the banner goes away the moment a password is set.
 */
import { ref } from 'vue'

/**
 * Bumped on every write here. Storage is not reactive, and the session lands
 * (onAuthStateChange, mid-verifyOtp) BEFORE the sign-in box records that it
 * was a code sign-in, so the banner must re-read when this moves, not only
 * when the user does. Seen on the deployed preview: flag written, no banner.
 */
export const setPasswordNudgeVersion = ref(0)

const CODE_SIGN_IN_KEY = 'ssi.codeSignIn.v1'
const DISMISSED_PREFIX = 'ssi.setPasswordNudge.dismissed.v1.'
export const DISMISS_DAYS = 30

function store(): Storage | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null } catch { return null }
}

export function markCodeSignIn(email: string): void {
  try { store()?.setItem(CODE_SIGN_IN_KEY, (email || '').trim().toLowerCase()) } catch { /* private mode */ }
  setPasswordNudgeVersion.value++
}

export function clearCodeSignIn(): void {
  try { store()?.removeItem(CODE_SIGN_IN_KEY) } catch { /* private mode */ }
  setPasswordNudgeVersion.value++
}

export function dismissSetPasswordNudge(userId: string, now = Date.now()): void {
  try { store()?.setItem(DISMISSED_PREFIX + userId, String(now)) } catch { /* private mode */ }
  setPasswordNudgeVersion.value++
}

interface NudgeUser {
  id?: string | null
  email?: string | null
  user_metadata?: Record<string, unknown> | null
}

export function shouldShowSetPasswordNudge(user: NudgeUser | null | undefined, now = Date.now()): boolean {
  if (!user?.id || !user.email) return false
  if (user.user_metadata?.has_password === true) return false
  const s = store()
  if (!s) return false
  if (s.getItem(CODE_SIGN_IN_KEY) !== user.email.trim().toLowerCase()) return false
  const dismissedAt = Number(s.getItem(DISMISSED_PREFIX + user.id) || 0)
  if (dismissedAt && now - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return false
  return true
}
